<template>
    <div class="my-2 overflow-hidden rounded-lg border border-violet-200 bg-white shadow-sm">
        <div class="flex items-center gap-2 border-b border-violet-100 bg-violet-50 px-4 py-2.5">
            <UIcon name="i-heroicons-pencil-square" class="h-4 w-4 text-violet-600" />
            <span class="text-sm font-semibold text-slate-800">{{ title }}</span>
            <span class="ml-auto text-xs" :class="isConfirmed ? 'text-emerald-600' : 'text-violet-600'">
                {{ isConfirmed ? confirmationLabel : isSaved ? "Saved — rewrite not queued" : reconfirmationRequired ? "Rewrite scope changed — confirm again" : "Review before confirming" }}
            </span>
        </div>

        <div class="px-4 py-3 text-sm leading-relaxed text-slate-700">
            <textarea
                v-if="action === 'publish' && !isSaved"
                v-model="draftWritingStyle"
                rows="8"
                maxlength="12000"
                class="w-full resize-y rounded-md border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed text-slate-700 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100 disabled:cursor-not-allowed disabled:bg-slate-50"
                :disabled="!canConfirm || saving"
                aria-label="Proposed Writing Style"
            />
            <div v-else-if="action === 'publish'" class="whitespace-pre-wrap break-words">{{ writingStyle }}</div>
            <p v-if="action === 'publish' && !isSaved" class="mt-3">
                Save this style organization-wide and queue rewrites for <strong>{{ hasAffectedCount ? affectedCount : "an uncounted number of" }}</strong> eligible untouched contact assignment{{ affectedCount === 1 ? "" : "s" }} across campaigns.
                Already-contacted people and their follow-ups are excluded. Sent messages stay unchanged.
            </p>
            <p v-else-if="action === 'regenerate'">
                Regenerate snippets for <strong>{{ affectedCount }}</strong> eligible stale unsent assignment{{ affectedCount === 1 ? "" : "s" }}. This reuses contact intake and does not rerun research or discovery.
            </p>
        </div>

        <div class="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50 px-4 py-3">
            <div class="min-w-0">
                <p class="text-xs text-slate-500">{{ explanation }}</p>
                <p v-if="error" class="mt-1 text-xs text-red-600">{{ error }}</p>
            </div>
            <button v-if="!isConfirmed" type="button" class="flex-none rounded-md bg-violet-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-slate-300" :disabled="!canConfirm || !proposalId || saving || !canPublish" @click="confirm">
                {{ saving ? "Confirming…" : actionLabel }}
            </button>
            <div v-else class="flex flex-none items-center gap-1.5 text-sm font-medium text-emerald-600">
                <UIcon name="i-heroicons-check-circle" class="h-4 w-4" />
                {{ confirmationLabel }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
const props = defineProps<{
    part: { output?: unknown };
    canConfirm: boolean;
    saving?: boolean;
    error?: string | null;
}>();
const emit = defineEmits<{ confirm: [writingStyle?: string] }>();
const output = computed(() => (typeof props.part.output === "object" && props.part.output != null && !Array.isArray(props.part.output) ? (props.part.output as Record<string, unknown>) : {}));
const proposalId = computed(() => (typeof output.value.proposalId === "string" ? output.value.proposalId : ""));
const action = computed(() => (output.value.action === "regenerate" ? "regenerate" : "publish"));
const writingStyle = computed(() => (typeof output.value.writingStyle === "string" ? output.value.writingStyle : "Preparing proposal…"));
const draftWritingStyle = ref("");
watch(writingStyle, value => {
    draftWritingStyle.value = value;
}, { immediate: true });
const canPublish = computed(() => action.value !== "publish" || draftWritingStyle.value.trim().length > 0);
const hasAffectedCount = computed(() => typeof output.value.affectedCount === "number");
const isSaved = computed(() => output.value.status === "published");
const rewritePending = computed(() => isSaved.value && output.value.regenerationStatus === "pending");
const affectedCount = computed(() => (typeof output.value.affectedCount === "number" ? output.value.affectedCount : 0));
const reconfirmationRequired = computed(() => output.value.reconfirmationRequired === true);
const isConfirmed = computed(() => (isSaved.value && !rewritePending.value) || output.value.status === "regeneration-started");
const confirmationLabel = computed(() => action.value === "regenerate" ? "Rewrite queued" : output.value.regenerationStatus === "queued" ? "Saved · rewrite queued" : "Style saved");
const title = computed(() => (action.value === "publish" ? "Proposed Writing Style" : "Regenerate Writing Style"));
const actionLabel = computed(() => rewritePending.value ? "Retry rewrite" : action.value === "publish" ? hasAffectedCount.value ? "Save & rewrite" : "Review rewrite count" : `Regenerate ${affectedCount.value}`);
const explanation = computed(() => {
    if (rewritePending.value) return "Style saved, but rewrites have not been confirmed as queued. Retry to queue the same eligible scope.";
    if (action.value === "publish" && isConfirmed.value) {
        if (output.value.regenerationStatus === "queued") return `Style saved. Rewrites queued for up to ${affectedCount.value} eligible untouched contact assignments, not completed yet.`;
        if (output.value.regenerationStatus === "not-required") return "Style saved. No existing contact assignments need an eligible rewrite.";
        return "Style saved. Existing messages were not rewritten by this confirmation.";
    }
    if (reconfirmationRequired.value) return "The eligible rewrite scope changed. Review the updated scope and count and confirm again; nothing has been saved or queued by this confirmation.";
    if (action.value === "publish") return "Applies to future generated messages too. Existing eligible messages are rewritten in the background without rerunning research or discovery.";
    if (isConfirmed.value) return "Rewrites queued, not completed yet. Sent message history is never changed.";
    return "Only active stale assignments are enqueued. Sent message history is never changed.";
});
function confirm() {
    if (!props.canConfirm || !proposalId.value || props.saving || isConfirmed.value || !canPublish.value) return;
    emit("confirm", action.value === "publish" ? draftWritingStyle.value : undefined);
}
</script>
